import React from 'react';
import { Card, Flex, Typography, Grid } from 'antd';
import { styled } from 'styled-components';
import { useSelector } from 'react-redux';
import { ArrowRightOutlined, RocketTwoTone } from '@ant-design/icons';
import { t } from '../../../../utils/i18n';
import type { AppState } from '../../../../declarations/redux';

const { useBreakpoint } = Grid;

const StyledUpgradeCard = styled(Card)`
  margin: 2rem;
  min-width: 20rem;
  cursor: pointer;
  background-color: #f5f5f5;
  transition: background-color 0.4s ease;

  &:hover {
    background-color: #ebebeb;
  }

  .ant-v5-card-body {
    padding: 1.5rem;
  }
`;

const StyledSlimUpgradeCard = styled(Card)`
  margin: 1.75rem;
  cursor: pointer;
  background-color: #f5f5f5;
  transition: background-color 0.4s ease;

  .ant-v5-card-body {
    padding: 0.5rem;
    display: flex;
    justify-content: center;
  }
`;

const StyledRocketIcon = styled(RocketTwoTone)`
  font-size: 2.5rem;
  transform: rotate(45deg);
`;

const StyledRightIcon = styled(ArrowRightOutlined)`
  font-size: 1.25rem;
  color: rgba(0, 0, 0, 0.45) !important;
`;

type UpgradeCardProps = { sideMenuCollapsed: boolean; onCardClick: () => void };

type UpgradeCardContent = {
  title: string;
  description: string;
  accentColor: string;
};

const getUpgradeCardContent = (
  isTrialMerchant: boolean,
  trialDaysLeft: number
): UpgradeCardContent => {
  if (isTrialMerchant && trialDaysLeft <= 4) {
    return {
      title: t('side-menu-cards.subscription-upgrade.trial-late.title', {
        days: trialDaysLeft,
      }),
      description: t(
        'side-menu-cards.subscription-upgrade.trial-late.description'
      ),
      accentColor: '#FF9800',
    };
  }

  if (isTrialMerchant && trialDaysLeft > 4) {
    return {
      title: t('side-menu-cards.subscription-upgrade.trial-early.title'),
      description: t(
        'side-menu-cards.subscription-upgrade.trial-early.description'
      ),
      accentColor: '#00d0be',
    };
  }

  return {
    title: t('side-menu-cards.subscription-upgrade.subscription.title'),
    description: t(
      'side-menu-cards.subscription-upgrade.subscription.description'
    ),
    accentColor: '#00d0be',
  };
};

export function SubscriptionUpgradeCard({
  sideMenuCollapsed,
  onCardClick,
}: UpgradeCardProps) {
  const screens = useBreakpoint();
  const currentMerchant = useSelector(
    (state: AppState) => state.currentMerchant
  );
  const isTrialMerchant = (currentMerchant?.trialDaysLeft || 0) > 0;
  const trialDaysLeft = currentMerchant?.trialDaysLeft || 0;

  const { title, description, accentColor } = getUpgradeCardContent(
    isTrialMerchant,
    trialDaysLeft
  );

  if (!screens.md || sideMenuCollapsed) {
    return (
      <StyledSlimUpgradeCard onClick={onCardClick}>
        <Flex gap={10} align="center" justify="flex-start">
          <StyledRocketIcon twoToneColor={accentColor} />
        </Flex>
      </StyledSlimUpgradeCard>
    );
  }

  return (
    <StyledUpgradeCard onClick={onCardClick}>
      <Flex gap={10} align="center" justify="flex-start">
        <StyledRocketIcon twoToneColor={accentColor} />
        <Typography.Text strong style={{ fontSize: 16 }}>
          {title}
        </Typography.Text>
      </Flex>
      <Flex align="flex-end" style={{ marginTop: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {description}
        </Typography.Text>
        <StyledRightIcon />
      </Flex>
    </StyledUpgradeCard>
  );
}
